import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import debounce from 'lodash/debounce'
import { orderRoute } from 'modules/order/routes'
import { paymentStatusOptions, statusOptions } from 'modules/order/types'

import { OrderListFilters, TOption } from '../types'

const createOptionsMap = (options: TOption[]): Map<string, TOption> =>
  new Map(options.map((option) => [String(option.name), option]))

const filterOptions = (names: string[] | undefined, optionsMap: Map<string, TOption>): TOption[] =>
  (names?.map((name) => optionsMap.get(name)).filter(Boolean) || []) as TOption[]

export const useOrderListState = () => {
  const navigate = useNavigate()
  const searchParams = orderRoute.useSearch()

  const departure = searchParams.departure || null
  const destination = searchParams.destination || null
  const departureDateFrom = searchParams.departureDateFrom || null
  const departureDateTo = searchParams.departureDateTo || null
  const search = searchParams.search || null
  const mode = searchParams.mode || 'filter'
  const page = searchParams.page || 1
  const perPage = searchParams.perPage || 10

  const statusOptionsMap = useMemo(() => createOptionsMap(statusOptions), [])
  const paymentStatusOptionsMap = useMemo(() => createOptionsMap(paymentStatusOptions), [])

  const status = useMemo(
    () => filterOptions(searchParams.status, statusOptionsMap),
    [searchParams.status, statusOptionsMap]
  )

  const paymentStatus = useMemo(
    () => filterOptions(searchParams.paymentStatus, paymentStatusOptionsMap),
    [searchParams.paymentStatus, paymentStatusOptionsMap]
  )

  const pagination = useMemo(
    () => ({
      page,
      perPage,
      total: 0
    }),
    [page, perPage]
  )

  const updateSearchParams = useCallback(
    (updates: Partial<OrderListFilters>) => {
      navigate({
        from: orderRoute.to,
        search: (prev: any) => {
          const newSearch = { ...prev, ...updates }

          Object.keys(newSearch).forEach((key) => {
            if (newSearch[key as keyof typeof newSearch] === undefined) {
              delete newSearch[key as keyof typeof newSearch]
            }
          })

          return newSearch
        }
      })
    },
    [navigate]
  )

  const updateSearchParamsRef = useRef(updateSearchParams)
  useEffect(() => {
    updateSearchParamsRef.current = updateSearchParams
  }, [updateSearchParams])

  const handleSetDeparture = useCallback(
    (value: string | null) => {
      updateSearchParams({ departure: value || undefined })
    },
    [updateSearchParams]
  )

  const handleSetDestination = useCallback(
    (value: string | null) => {
      updateSearchParams({ destination: value || undefined })
    },
    [updateSearchParams]
  )

  const handleSetDepartureDateFrom = useCallback(
    (value: string | null) => {
      updateSearchParams({ departureDateFrom: value || undefined })
    },
    [updateSearchParams]
  )

  const handleSetDepartureDateTo = useCallback(
    (value: string | null) => {
      updateSearchParams({ departureDateTo: value || undefined })
    },
    [updateSearchParams]
  )

  const handleSetStatus = useCallback(
    (options: TOption[] | null) => {
      updateSearchParams({
        status: options?.length ? options.map((opt) => opt.name) : undefined
      })
    },
    [updateSearchParams]
  )

  const handleSetPaymentStatus = useCallback(
    (options: TOption[] | null) => {
      updateSearchParams({
        paymentStatus: options?.length ? options.map((opt) => opt.name) : undefined
      })
    },
    [updateSearchParams]
  )

  const debouncedSetSearch = useMemo(
    () =>
      debounce(
        (value: string | null) => {
          let updates = { search: value || undefined } as { page?: number; search?: string }

          if (value && value.length >= 3) {
            updates = { ...updates, page: 1 }
          }

          updateSearchParamsRef.current(updates)
        },
        500,
        { trailing: true, leading: false }
      ),
    []
  )

  useEffect(
    () => () => {
      debouncedSetSearch.cancel()
    },
    [debouncedSetSearch]
  )

  const handleSetSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()

      debouncedSetSearch(trimmedValue || null)
    },
    [debouncedSetSearch]
  )

  const handleSetMode = useCallback(
    (newMode: 'filter' | 'search') => {
      debouncedSetSearch.cancel()
      updateSearchParams({ mode: newMode, page: 1 })
    },
    [updateSearchParams, debouncedSetSearch]
  )

  const handleSetPagination = useCallback(
    (updates: { page?: number; perPage?: number }) => {
      updateSearchParams(updates)
    },
    [updateSearchParams]
  )

  const handleResetFilter = useCallback(() => {
    debouncedSetSearch.cancel()
    navigate({
      from: orderRoute.to,
      search: ({ search }) => ({
        search,
        page: 1,
        perPage: pagination.perPage,
        mode: 'filter'
      })
    })
  }, [navigate, pagination.perPage, debouncedSetSearch])

  const handleSwapPoints = useCallback(() => {
    updateSearchParams({
      departure: destination || undefined,
      destination: departure || undefined
    })
  }, [departure, destination, updateSearchParams])

  return {
    departure,
    destination,
    departureDateFrom,
    departureDateTo,
    status,
    paymentStatus,
    search,
    mode,
    pagination,

    handleSetDeparture,
    handleSetDestination,
    handleSetDepartureDateFrom,
    handleSetDepartureDateTo,
    handleSetStatus,
    handleSetPaymentStatus,
    handleSetSearch,
    handleSetMode,
    handleSetPagination,
    handleResetFilter,
    handleSwapPoints,
    updateSearchParams
  }
}
